import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { Btn, Field, Input, Select } from "./kit";

export function useBlob(url) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!url) return undefined;
    let u;
    api.get(url, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setSrc(u); }).catch(() => setSrc(null));
    return () => u && URL.revokeObjectURL(u);
  }, [url]);
  return src;
}

const pct = (e, el) => { const r = el.getBoundingClientRect(); return { x: Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100)), y: Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100)) }; };
const boxOf = (a, b) => ({ x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) });
const r1 = (b) => Object.fromEntries(Object.entries(b).map(([k, v]) => [k, Math.round(v * 10) / 10]));

function Box({ b, n, active }) {
  return <span className={`pointer-events-none absolute border-2 ${active ? "border-dashed border-clay" : "border-clay bg-clay/[0.12]"}`}
    style={{ left: `${b.x}%`, top: `${b.y}%`, width: `${b.w}%`, height: `${b.h}%` }}>{n && <b className="absolute -left-0.5 -top-6 rounded bg-clay px-1.5 text-xs text-white">{n}</b>}</span>;
}

export function RegionPicker({ base, files, onAdd }) {
  const { t } = useTranslation();
  const photos = files.filter((f) => f.kind === "photo");
  const [fid, setFid] = useState(photos[0]?.id || "");
  const [start, setStart] = useState(null);
  const [box, setBox] = useState(null);
  const [text, setText] = useState("");
  const ref = useRef(null);
  const src = useBlob(fid ? `${base}/files/${fid}/content` : null);
  if (!photos.length) return <p className="text-sm" data-testid="region-no-photos">{t("m.no_photos")}</p>;
  const down = (e) => { e.preventDefault(); e.currentTarget.setPointerCapture?.(e.pointerId); const p = pct(e, ref.current); setStart(p); setBox({ ...p, w: 0, h: 0 }); };
  const move = (e) => { if (start) setBox(boxOf(start, pct(e, ref.current))); };
  const up = (e) => { if (start) { const b = boxOf(start, pct(e, ref.current)); setBox(b.w < 2 || b.h < 2 ? null : b); setStart(null); } };
  const add = () => { onAdd({ file_id: fid, region: r1(box), text: text.trim() }); setBox(null); setText(""); };
  return (
    <div className="space-y-2" data-testid="region-picker">
      <Select aria-label={t("m.pick_photo")} data-testid="region-file" value={fid} onChange={(e) => { setFid(e.target.value); setBox(null); }}>{photos.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}</Select>
      <p className="text-xs">{t("m.draw_hint")}</p>
      <div ref={ref} className="relative w-full touch-none select-none overflow-hidden rounded-xl bg-muted" onPointerDown={down} onPointerMove={move} onPointerUp={up} data-testid="region-canvas">
        {src ? <img src={src} alt={t("m.pick_photo")} className="block w-full" draggable={false} /> : <div className="aspect-video" />}
        {box && <Box b={box} active={!!start} />}
      </div>
      {box && !start && <p className="text-xs" data-testid="region-coords">{t("m.region_sel", r1(box))}</p>}
      <Field label={t("m.r_pc")} htmlFor="pc-text"><Input id="pc-text" data-testid="region-text" value={text} onChange={(e) => setText(e.target.value)} /></Field>
      <Btn variant="outline" testid="region-add" disabled={!box || start || !text.trim()} onClick={add}>{t("m.attach_region")}</Btn>
    </div>
  );
}

export function TimePicker({ base, files, onAdd }) {
  const { t } = useTranslation();
  const vids = files.filter((f) => f.kind === "video");
  const [fid, setFid] = useState(vids[0]?.id || "");
  const [text, setText] = useState("");
  const [at, setAt] = useState(0);
  const ref = useRef(null);
  const src = useBlob(fid ? `${base}/files/${fid}/content` : null);
  if (!vids.length) return <p className="text-sm" data-testid="time-no-videos">{t("m.no_videos")}</p>;
  const add = () => { onAdd({ file_id: fid, at_sec: Math.round((ref.current?.currentTime ?? at) * 10) / 10, text: text.trim() }); setText(""); };
  return (
    <div className="space-y-2" data-testid="time-picker">
      <Select aria-label={t("m.pick_video")} data-testid="time-file" value={fid} onChange={(e) => setFid(e.target.value)}>{vids.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}</Select>
      <p className="text-xs">{t("m.time_hint")}</p>
      {src ? <video ref={ref} src={src} controls playsInline className="aspect-video w-full rounded-xl bg-black" data-testid="time-video" onTimeUpdate={(e) => setAt(e.currentTarget.currentTime)} onPause={(e) => setAt(e.currentTarget.currentTime)} /> : <div className="aspect-video rounded-xl bg-muted" />}
      <p className="text-sm font-semibold" data-testid="time-current">{t("m.at_time", { s: at.toFixed(1) })}</p>
      <Field label={t("m.r_vc")} htmlFor="vc-text"><Input id="vc-text" data-testid="time-text" value={text} onChange={(e) => setText(e.target.value)} /></Field>
      <Btn variant="outline" testid="time-add" disabled={!text.trim()} onClick={add}>{t("m.attach_time")}</Btn>
    </div>
  );
}

function PhotoWithBoxes({ base, fid, items, offset }) {
  const src = useBlob(`${base}/files/${fid}/content`);
  return <div className="relative w-full overflow-hidden rounded-xl bg-muted" data-testid={`photo-markers-${fid}`}>
    {src ? <img src={src} alt="" className="block w-full" /> : <div className="aspect-video" />}
    {items.map((c, i) => <Box key={i} b={c.region} n={offset + i + 1} />)}</div>;
}

export function PhotoMarkers({ base, comments }) {
  const { t } = useTranslation();
  if (!comments?.length) return null;
  const groups = comments.reduce((g, c) => ({ ...g, [c.file_id || "_"]: [...(g[c.file_id || "_"] || []), c] }), {});
  let n = 0;
  return <div className="space-y-2" data-testid="photo-markers"><b>{t("m.photo_comments")}</b>{Object.entries(groups).map(([fid, items]) => {
    const off = n; n += items.length;
    return <div key={fid} className="space-y-1">{fid !== "_" && <PhotoWithBoxes base={base} fid={fid} items={items} offset={off} />}
      <ol className="ml-5 list-decimal" start={off + 1}>{items.map((c, i) => <li key={i} data-testid="photo-marker-text">{c.text}</li>)}</ol></div>;
  })}</div>;
}

function VideoWithMarks({ base, fid, items }) {
  const { t } = useTranslation();
  const src = useBlob(`${base}/files/${fid}/content`);
  const ref = useRef(null);
  return <div className="space-y-1">{src ? <video ref={ref} src={src} controls playsInline className="aspect-video w-full rounded-xl bg-black" /> : <div className="aspect-video rounded-xl bg-muted" />}
    <ul className="flex flex-wrap gap-2">{items.map((c, i) => <li key={i}><Btn variant="outline" testid={`video-marker-${i}`} onClick={() => { if (ref.current) { ref.current.currentTime = c.at_sec; ref.current.play?.().catch(() => {}); } }}>
      ▶ {t("m.at_time", { s: c.at_sec })} — {c.text}</Btn></li>)}</ul></div>;
}

export function VideoMarkers({ base, comments }) {
  const { t } = useTranslation();
  if (!comments?.length) return null;
  const groups = comments.reduce((g, c) => ({ ...g, [c.file_id || "_"]: [...(g[c.file_id || "_"] || []), c] }), {});
  return <div className="space-y-2" data-testid="video-markers"><b>{t("m.video_comments")}</b>{Object.entries(groups).map(([fid, items]) => fid === "_"
    ? <ul key={fid}>{items.map((c, i) => <li key={i}>▶ {t("m.at_time", { s: c.at_sec })} — {c.text}</li>)}</ul> : <VideoWithMarks key={fid} base={base} fid={fid} items={items} />)}</div>;
}
